import React, { useState } from "react";
import {
  Upload,
  Button,
  message,
  Progress,
  Alert,
  Typography,
  Space,
  Card,
  List,
  Tag,
} from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../../../components/form/form";
import { FormRow } from "../../../../../components/form/form-row";
import {
  DownloadOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import type { UploadProps, UploadFile } from "antd";
import useFetch from "../../../../../hooks/useFetch";
import useToast from "../../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../../state/toast/slice";
import { eResultCode } from "../../../../../utils/enum";
import styles from "./upload-excel.module.css";
import { UploadTaskInBulk } from "../../../../../utils/api.constant";

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

interface UploadExcelProps {
  projectId: string;
  projectName?: string;
  projectCategoryId?: number;
  onCloseDrawer: () => void;
  onRefreshList?: () => void;
}

interface UploadResult {
  success: boolean;
  message: string;
  totalRows?: number;
  successRows?: number;
  errorRows?: number;
  errors?: string[];
}

const UploadExcel: React.FC<UploadExcelProps> = ({
  projectId,
  projectName,
  projectCategoryId,
  onCloseDrawer,
  onRefreshList,
}) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const { post } = useFetch();
  const { onShowToast } = useToast();

  const downloadTemplate = async () => {
    try {
      const templateUrl = "/assets/templates/uploadTaskInBulk.xlsx";
      const link = document.createElement("a");
      link.href = templateUrl;
      link.download = "task-upload-template.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success("Template downloaded successfully");
    } catch (error) {
      message.error("Failed to download template");
    }
  };

  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    fileList,
    beforeUpload: (file) => {
      const isExcel =
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls");

      if (!isExcel) {
        message.error("You can only upload Excel files (.xlsx, .xls)!");
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("File must be smaller than 10MB!");
        return false;
      }

      setFileList([file]);
      return false; // Prevent automatic upload
    },
    onRemove: () => {
      setFileList([]);
      setUploadResult(null);
    },
    showUploadList: {
      showPreviewIcon: false,
      showRemoveIcon: true,
    },
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning("Please select an Excel file to upload");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);

    try {
      const formData = new FormData();
      const file = (fileList[0].originFileObj || fileList[0]) as File;
      formData.append("file", file);
      formData.append("projectId", projectId);

      // Add project category ID if available
      if (projectCategoryId) {
        formData.append("projectCategoryId", projectCategoryId.toString());
      }

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Use the same endpoint as the reference code
      const response = await post(UploadTaskInBulk, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        const result: UploadResult = {
          success: true,
          message: "Excel file uploaded successfully",
          totalRows: response.data?.totalRows || 0,
          successRows: response.data?.successRows || 0,
          errorRows: response.data?.errorRows || 0,
          errors: response.data?.errors || [],
        };

        setUploadResult(result);

        onShowToast({
          type: ToastType.success,
          title: <CheckCircleOutlined />,
          position: ToastOpen.centerTop,
          content: "Tasks uploaded successfully",
        });

        // Refresh the dashboard
        if (onRefreshList) {
          onRefreshList();
        }
      } else {
        const result: UploadResult = {
          success: false,
          message: response.dataResponse.description || "Upload failed",
          errors: response.data?.errors || [],
        };

        setUploadResult(result);

        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: "Failed to upload tasks",
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      setUploadResult({
        success: false,
        message: "An error occurred during upload",
      });

      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Upload failed due to network error",
      });
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setFileList([]);
    setUploadResult(null);
    setUploadProgress(0);
  };

  return (
    <FormLayout>
      <FormHeader>
        <CloseOutlined
          style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
          onClick={onCloseDrawer}
        />
      </FormHeader>
      <FormContent>
        <div className={styles.headerSection}>
          <div className={styles.titleSection}>
            <FileExcelOutlined className={styles.titleIcon} />
            <div className={styles.titleContent}>
              <Title
                level={3}
                className={styles.title}
              >
                Upload Tasks from Excel
              </Title>
              <Text className={styles.subtitle}>
                Upload multiple tasks to {projectName || "the selected project"}{" "}
                using an Excel file
              </Text>
            </div>
          </div>
        </div>

        <div className={styles.content}>
          {/* Instructions */}
          <Card className={styles.instructionsCard}>
            <Title
              level={4}
              className={styles.cardTitle}
            >
              Instructions
            </Title>
            <List
              size='small'
              dataSource={[
                "Download the Excel template using the button below",
                "Fill in the task details in the template",
                "Make sure all required fields are completed",
                "Upload the completed Excel file",
                "Review the upload results and fix any errors if needed",
              ]}
              renderItem={(item, index) => (
                <List.Item className={styles.instructionItem}>
                  <Text>
                    <span className={styles.stepNumber}>{index + 1}.</span>
                    {item}
                  </Text>
                </List.Item>
              )}
            />
          </Card>

          {/* Template Download */}
          <Card className={styles.templateCard}>
            <div className={styles.templateSection}>
              <div className={styles.templateInfo}>
                <Title
                  level={4}
                  className={styles.cardTitle}
                >
                  Excel Template
                </Title>
                <Paragraph className={styles.templateDescription}>
                  Download the template to ensure your Excel file has the
                  correct format and required columns.
                </Paragraph>
              </div>
              <Button
                type='primary'
                icon={<DownloadOutlined />}
                onClick={downloadTemplate}
                className={styles.downloadButton}
              >
                Download Template
              </Button>
            </div>
          </Card>

          {/* File Upload */}
          <Card className={styles.uploadCard}>
            <Title
              level={4}
              className={styles.cardTitle}
            >
              Upload Excel File
            </Title>

            <Dragger
              {...uploadProps}
              className={styles.dragger}
            >
              <p className={styles.dragIcon}>
                <FileExcelOutlined />
              </p>
              <p className={styles.dragText}>
                Click or drag Excel file to this area to upload
              </p>
              <p className={styles.dragHint}>
                Support for .xlsx and .xls files. Maximum file size: 10MB
              </p>
            </Dragger>

            {uploading && (
              <div className={styles.progressSection}>
                <Progress
                  percent={uploadProgress}
                  status='active'
                  strokeColor={{
                    "0%": "#4b5563",
                    "100%": "#1f2937",
                  }}
                />
                <Text className={styles.progressText}>
                  Processing your Excel file...
                </Text>
              </div>
            )}
          </Card>

          {/* Upload Results */}
          {uploadResult && (
            <Card className={styles.resultCard}>
              <Title
                level={4}
                className={styles.cardTitle}
              >
                Upload Results
              </Title>

              <Alert
                message={
                  uploadResult.success ? "Upload Successful" : "Upload Failed"
                }
                description={uploadResult.message}
                type={uploadResult.success ? "success" : "error"}
                showIcon
                className={styles.resultAlert}
              />

              {uploadResult.success && (
                <div className={styles.resultStats}>
                  <Space size='large'>
                    <div className={styles.stat}>
                      <Text className={styles.statLabel}>Total Rows</Text>
                      <Text className={styles.statValue}>
                        {uploadResult.totalRows}
                      </Text>
                    </div>
                    <div className={styles.stat}>
                      <Text className={styles.statLabel}>Successful</Text>
                      <Text
                        className={styles.statValue}
                        style={{ color: "#52c41a" }}
                      >
                        {uploadResult.successRows}
                      </Text>
                    </div>
                    {uploadResult.errorRows! > 0 && (
                      <div className={styles.stat}>
                        <Text className={styles.statLabel}>Errors</Text>
                        <Text
                          className={styles.statValue}
                          style={{ color: "#ff4d4f" }}
                        >
                          {uploadResult.errorRows}
                        </Text>
                      </div>
                    )}
                  </Space>
                </div>
              )}

              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className={styles.errorSection}>
                  <Title
                    level={5}
                    className={styles.errorTitle}
                  >
                    <ExclamationCircleOutlined /> Errors Found
                  </Title>
                  <List
                    size='small'
                    dataSource={uploadResult.errors}
                    renderItem={(error, index) => (
                      <List.Item className={styles.errorItem}>
                        <Tag color='error'>Row {index + 1}</Tag>
                        <Text>{error}</Text>
                      </List.Item>
                    )}
                    className={styles.errorList}
                  />
                </div>
              )}
            </Card>
          )}
        </div>
      </FormContent>
      <FormFooter className='formFooter'>
        <Button
          type='default'
          disabled={uploading}
          className='btnDimensions'
          onClick={onCloseDrawer}
        >
          Close
        </Button>
        <Button
          type='primary'
          className='btnDimensions'
          onClick={handleUpload}
          loading={uploading}
          disabled={uploading || fileList.length === 0}
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </FormFooter>
    </FormLayout>
  );
};

export default UploadExcel;
